"use client";

import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { synapticIdentityConfig, getLoginUrl, getRegisterUrl, type SynapticUser } from "@/lib/auth/synaptic-identity";
import { createAuthPermissions, type AuthPermissions } from "@/lib/auth/permissions";
import { AuthProvider as SharedAuthProvider, useAuth as useSharedAuth } from "@custom-packages/auth-core";

export type UserRole = "guest" | "member" | "administrator" | "tenant_admin" | "super_admin";

interface AuthContextProps {
  user: SynapticUser | null;
  token: string | null;
  role: UserRole;
  permissions: AuthPermissions;
  isLoading: boolean;
  login: () => void;
  signup: () => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextProps | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <SharedAuthProvider storageKey="jwt">
      <AuthContextWrapper>{children}</AuthContextWrapper>
    </SharedAuthProvider>
  );
};

const AuthContextWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const shared = useSharedAuth();
  const [profile, setProfile] = useState<{ token: string; user: SynapticUser | null } | null>(null);
  const syncedUser = shared.token && profile?.token === shared.token ? profile?.user ?? null : null;
  const isProfileLoading = Boolean(shared.token) && profile?.token !== shared.token;

  // Fallback to shared.user (decoded JWT) if the /auth/me fetch hasn't completed or failed
  const activeUser = syncedUser || (shared.user ? {
    id: shared.user.id || "",
    email: shared.user.email || "",
    name: shared.user.name,
    role: shared.user.role,
    roles: Array.isArray((shared.user as unknown as { roles?: unknown }).roles)
      ? (shared.user as unknown as { roles: unknown[] }).roles.map(String)
      : undefined,
  } as SynapticUser : null);

  const permissions = useMemo(
    () => createAuthPermissions(
      syncedUser as unknown as Record<string, unknown> | null,
      shared.user as unknown as Record<string, unknown> | null,
    ),
    [syncedUser, shared.user],
  );

  const role: UserRole = permissions.hasRole("administrator") ||
                         permissions.hasRole("tenant_admin") ||
                         permissions.hasRole("super_admin")
    ? "administrator"
    : permissions.hasRole("member")
    ? "member"
    : "guest";

  const getCurrentPath = () => {
    if (typeof window === "undefined") return "/";
    return window.location.pathname + window.location.search;
  };

  const login = () => {
    if (typeof window !== "undefined") {
      window.location.href = getLoginUrl(getCurrentPath());
    }
  };

  const signup = () => {
    if (typeof window !== "undefined") {
      window.location.href = getRegisterUrl(getCurrentPath());
    }
  };

  const logout = () => {
    shared.logout();
    setProfile(null);
    if (typeof window !== "undefined") {
      window.location.href = "/";
    }
  };

  // Sync /auth/me user profile from Synaptic Identity API whenever token changes
  useEffect(() => {
    const token = shared.token;
    if (!token) {
      return;
    }

    let active = true;
    fetch(`${synapticIdentityConfig.apiBase}/auth/me`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (active) {
          setProfile({ token, user: data?.user || null });
        }
      })
      .catch(() => {
        if (active) {
          setProfile({ token, user: null });
        }
      });

    return () => {
      active = false;
    };
  }, [shared.token, shared.isLoading]);

  return (
    <AuthContext.Provider
      value={{
        user: activeUser,
        token: shared.token,
        role,
        permissions,
        isLoading: shared.isLoading || isProfileLoading,
        login,
        signup,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
